import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Trash2, Edit2, Save, X, Wine, UtensilsCrossed, LogOut } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const WINE_CATEGORIES = [
  "Champagne",
  "Sparkling", 
  "White - Light & Crisp",
  "White - Rich & Full",
  "White - Aromatic",
  "Orange / Skin Contact",
  "Rosé",
  "Red - Light & Elegant",
  "Red - Medium Body",
  "Red - Full & Bold",
  "Dessert",
  "Fortified"
];

const MENU_CATEGORIES = [
  "Appetizers",
  "Salads",
  "Mains",
  "Seafood",
  "Meat",
  "Vegetarian",
  "Sides",
  "Desserts"
];

const AdminPanel = ({ restaurant }) => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  
  const [activeTab, setActiveTab] = useState('wines');
  const [wines, setWines] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [editingWine, setEditingWine] = useState(null);
  const [editingMenuItem, setEditingMenuItem] = useState(null);
  const [showAddWine, setShowAddWine] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  
  const [newWine, setNewWine] = useState({ name: '', glass_price: '', bottle_price: '', category: WINE_CATEGORIES[0], in_stock: true });
  const [newMenuItem, setNewMenuItem] = useState({ name: '', description: '', price: '', category: MENU_CATEGORIES[0] });

  const restaurantName = restaurant === 'treadwell' ? 'Treadwell Cuisine' : 'NOTL';

  // Check for existing token on mount
  useEffect(() => {
    const savedToken = localStorage.getItem(`admin_token_${restaurant}`);
    if (savedToken) {
      setToken(savedToken);
      setIsLoggedIn(true);
    }
  }, [restaurant]);

  // Fetch data when logged in
  useEffect(() => {
    if (isLoggedIn && token) {
      fetchWines();
      fetchMenuItems();
    }
  }, [isLoggedIn, token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const response = await axios.post(`${API}/api/admin/login`, { username, password });
      if (response.data.restaurant === restaurant) {
        setToken(response.data.token);
        localStorage.setItem(`admin_token_${restaurant}`, response.data.token);
        setIsLoggedIn(true);
      } else {
        setLoginError('Invalid credentials for this restaurant');
      }
    } catch (error) {
      setLoginError('Invalid username or password');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(`admin_token_${restaurant}`);
    setToken('');
    setIsLoggedIn(false);
  };

  const fetchWines = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/wines`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWines(response.data);
    } catch (error) {
      console.error('Error fetching wines:', error);
    }
  };

  const fetchMenuItems = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/menu`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMenuItems(response.data);
    } catch (error) {
      console.error('Error fetching menu:', error);
    }
  };

  // Wine CRUD
  const addWine = async () => {
    if (!newWine.name || !newWine.bottle_price) return;
    try {
      await axios.post(`${API}/api/admin/wines`, {
        ...newWine,
        glass_price: newWine.glass_price ? parseFloat(newWine.glass_price) : null,
        bottle_price: parseFloat(newWine.bottle_price),
        in_stock: newWine.in_stock
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewWine({ name: '', glass_price: '', bottle_price: '', category: WINE_CATEGORIES[0], in_stock: true });
      setShowAddWine(false);
      fetchWines();
    } catch (error) {
      console.error('Error adding wine:', error);
    }
  };

  const updateWine = async (wine) => {
    try {
      await axios.put(`${API}/api/admin/wines/${wine.id}`, {
        name: wine.name,
        glass_price: wine.glass_price ? parseFloat(wine.glass_price) : null,
        bottle_price: parseFloat(wine.bottle_price),
        category: wine.category,
        in_stock: wine.in_stock
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingWine(null);
      fetchWines();
    } catch (error) {
      console.error('Error updating wine:', error);
    }
  };

  const toggleWineStock = async (wine) => {
    try {
      await axios.put(`${API}/api/admin/wines/${wine.id}`, {
        in_stock: !wine.in_stock
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchWines();
    } catch (error) {
      console.error('Error toggling stock:', error);
    }
  };

  const deleteWine = async (id) => {
    if (!window.confirm('Delete this wine?')) return;
    try {
      await axios.delete(`${API}/api/admin/wines/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchWines();
    } catch (error) {
      console.error('Error deleting wine:', error);
    }
  };

  // Menu CRUD
  const addMenuItem = async () => {
    if (!newMenuItem.name || !newMenuItem.price) return;
    try {
      await axios.post(`${API}/api/admin/menu`, {
        ...newMenuItem,
        price: parseFloat(newMenuItem.price)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewMenuItem({ name: '', description: '', price: '', category: MENU_CATEGORIES[0] });
      setShowAddMenu(false);
      fetchMenuItems();
    } catch (error) {
      console.error('Error adding menu item:', error);
    }
  };

  const updateMenuItem = async (item) => {
    try {
      await axios.put(`${API}/api/admin/menu/${item.id}`, {
        name: item.name,
        description: item.description,
        price: parseFloat(item.price),
        category: item.category
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingMenuItem(null);
      fetchMenuItems();
    } catch (error) {
      console.error('Error updating menu item:', error);
    }
  };

  const deleteMenuItem = async (id) => {
    if (!window.confirm('Delete this menu item?')) return;
    try {
      await axios.delete(`${API}/api/admin/menu/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchMenuItems();
    } catch (error) {
      console.error('Error deleting menu item:', error);
    }
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex flex-col items-center leading-none mb-4">
              <span className="text-[10px] font-medium tracking-[0.3em] text-gray-400">THE</span>
              <span className="text-2xl font-semibold tracking-wide" style={{fontFamily: 'Georgia, serif', color: '#722F37'}}>SOMM</span>
            </div>
            <h1 className="text-xl font-medium text-gray-900">{restaurantName} Admin</h1>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#D4704A] focus:border-transparent"
                placeholder="Enter username"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#D4704A] focus:border-transparent"
                placeholder="Enter password"
              />
            </div>
            {loginError && <p className="text-red-500 text-sm">{loginError}</p>}
            <button
              type="submit"
              className="w-full py-3 bg-[#D4704A] text-white rounded-xl font-medium hover:bg-[#B85A3A] transition-colors"
            >
              Sign In
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="flex flex-col items-center leading-none">
              <span className="text-[8px] font-medium tracking-[0.2em] text-gray-400">THE</span>
              <span className="text-lg font-semibold tracking-wide" style={{fontFamily: 'Georgia, serif', color: '#722F37'}}>SOMM</span>
            </div>
            <div className="h-8 w-px bg-gray-200"></div>
            <span className="text-sm font-medium text-gray-600">{restaurantName} Admin</span>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-4 py-4">
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('wines')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-colors ${
              activeTab === 'wines' 
                ? 'bg-[#D4704A] text-white' 
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Wine className="h-4 w-4" />
            Wine List ({wines.filter(w => w.in_stock !== false).length}/{wines.length})
          </button>
          <button
            onClick={() => setActiveTab('menu')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-colors ${
              activeTab === 'menu' 
                ? 'bg-[#D4704A] text-white' 
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <UtensilsCrossed className="h-4 w-4" />
            Food Menu ({menuItems.length})
          </button>
        </div>

        {/* Wine List Tab */}
        {activeTab === 'wines' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-lg font-medium text-gray-900">Wine List</h2>
              <button
                onClick={() => setShowAddWine(true)}
                className="flex items-center gap-2 px-4 py-2 bg-[#D4704A] text-white rounded-xl text-sm font-medium hover:bg-[#B85A3A]"
              >
                <Plus className="h-4 w-4" />
                Add Wine
              </button>
            </div>

            {/* Add Wine Form */}
            {showAddWine && (
              <div className="p-4 bg-gray-50 border-b border-gray-100">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                  <div className="md:col-span-2">
                    <input
                      type="text"
                      placeholder="Wine name (include vintage & region)"
                      value={newWine.name}
                      onChange={(e) => setNewWine({...newWine, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    />
                  </div>
                  <input
                    type="number"
                    placeholder="Glass $"
                    value={newWine.glass_price}
                    onChange={(e) => setNewWine({...newWine, glass_price: e.target.value})}
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                  <input
                    type="number"
                    placeholder="Bottle $"
                    value={newWine.bottle_price}
                    onChange={(e) => setNewWine({...newWine, bottle_price: e.target.value})}
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                  <select
                    value={newWine.category}
                    onChange={(e) => setNewWine({...newWine, category: e.target.value})}
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  >
                    {WINE_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={addWine} className="px-4 py-2 bg-[#D4704A] text-white rounded-lg text-sm font-medium">
                    Save Wine
                  </button>
                  <button onClick={() => setShowAddWine(false)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Wine List */}
            <div className="divide-y divide-gray-100">
              {wines.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No wines yet. Add your first wine above.
                </div>
              ) : (
                wines.map(wine => (
                  <div key={wine.id} className="p-4 hover:bg-gray-50">
                    {editingWine?.id === wine.id ? (
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-center">
                        <div className="md:col-span-2">
                          <input
                            type="text"
                            value={editingWine.name}
                            onChange={(e) => setEditingWine({...editingWine, name: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                          />
                        </div>
                        <input
                          type="number"
                          value={editingWine.glass_price || ''}
                          onChange={(e) => setEditingWine({...editingWine, glass_price: e.target.value})}
                          className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                          placeholder="Glass $"
                        />
                        <input
                          type="number"
                          value={editingWine.bottle_price}
                          onChange={(e) => setEditingWine({...editingWine, bottle_price: e.target.value})}
                          className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                        />
                        <div className="flex gap-2">
                          <button onClick={() => updateWine(editingWine)} className="p-2 bg-green-500 text-white rounded-lg">
                            <Save className="h-4 w-4" />
                          </button>
                          <button onClick={() => setEditingWine(null)} className="p-2 bg-gray-200 text-gray-700 rounded-lg">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-between items-center">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className={`font-medium ${wine.in_stock === false ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{wine.name}</p>
                            {wine.in_stock === false && (
                              <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">Out of Stock</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">
                            {wine.category} • {wine.glass_price ? `$${wine.glass_price} glass / ` : ''}${wine.bottle_price} bottle
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => toggleWineStock(wine)} 
                            className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                              wine.in_stock === false 
                                ? 'bg-gray-200 text-gray-600 hover:bg-green-100 hover:text-green-700' 
                                : 'bg-green-100 text-green-700 hover:bg-red-100 hover:text-red-600'
                            }`}
                          >
                            {wine.in_stock === false ? 'Mark In Stock' : 'In Stock'}
                          </button>
                          <button onClick={() => setEditingWine(wine)} className="p-2 text-gray-400 hover:text-[#D4704A]">
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button onClick={() => deleteWine(wine.id)} className="p-2 text-gray-400 hover:text-red-500">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Food Menu Tab */}
        {activeTab === 'menu' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-lg font-medium text-gray-900">Food Menu</h2>
              <button
                onClick={() => setShowAddMenu(true)}
                className="flex items-center gap-2 px-4 py-2 bg-[#D4704A] text-white rounded-xl text-sm font-medium hover:bg-[#B85A3A]"
              >
                <Plus className="h-4 w-4" />
                Add Item
              </button>
            </div>

            {/* Add Menu Item Form */}
            {showAddMenu && (
              <div className="p-4 bg-gray-50 border-b border-gray-100">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <input
                    type="text"
                    placeholder="Item name"
                    value={newMenuItem.name}
                    onChange={(e) => setNewMenuItem({...newMenuItem, name: e.target.value})}
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Description (optional)"
                    value={newMenuItem.description}
                    onChange={(e) => setNewMenuItem({...newMenuItem, description: e.target.value})}
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                  <input
                    type="number"
                    placeholder="Price $"
                    value={newMenuItem.price}
                    onChange={(e) => setNewMenuItem({...newMenuItem, price: e.target.value})}
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                  <select
                    value={newMenuItem.category}
                    onChange={(e) => setNewMenuItem({...newMenuItem, category: e.target.value})}
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  >
                    {MENU_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={addMenuItem} className="px-4 py-2 bg-[#D4704A] text-white rounded-lg text-sm font-medium">
                    Save Item
                  </button>
                  <button onClick={() => setShowAddMenu(false)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Menu List */}
            <div className="divide-y divide-gray-100">
              {menuItems.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No menu items yet. Add your first item above.
                </div>
              ) : (
                menuItems.map(item => (
                  <div key={item.id} className="p-4 hover:bg-gray-50">
                    {editingMenuItem?.id === item.id ? (
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-center">
                        <input
                          type="text"
                          value={editingMenuItem.name}
                          onChange={(e) => setEditingMenuItem({...editingMenuItem, name: e.target.value})}
                          className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                        />
                        <input
                          type="text"
                          value={editingMenuItem.description || ''}
                          onChange={(e) => setEditingMenuItem({...editingMenuItem, description: e.target.value})}
                          className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                        />
                        <input
                          type="number"
                          value={editingMenuItem.price}
                          onChange={(e) => setEditingMenuItem({...editingMenuItem, price: e.target.value})}
                          className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                        />
                        <div className="flex gap-2">
                          <button onClick={() => updateMenuItem(editingMenuItem)} className="p-2 bg-green-500 text-white rounded-lg">
                            <Save className="h-4 w-4" />
                          </button>
                          <button onClick={() => setEditingMenuItem(null)} className="p-2 bg-gray-200 text-gray-700 rounded-lg">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-between items-center">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{item.name}</p>
                          <p className="text-sm text-gray-500">
                            {item.category} • ${item.price}
                            {item.description && ` • ${item.description}`}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => setEditingMenuItem(item)} className="p-2 text-gray-400 hover:text-[#D4704A]">
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button onClick={() => deleteMenuItem(item.id)} className="p-2 text-gray-400 hover:text-red-500">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
